import React, { useState } from 'react';
import { ListLabel } from '../Components';

import { List, ListItem, ListItemText, Typography, IconButton, Button, Stack, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Info, OpenInNew } from '@mui/icons-material';
import { GetGuideQuery } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { useModal } from '../dialogs/useModal';
import { RankDetailsDialog, RankDetailsDialogProps } from './RankDetailsDialog';
import { LicensePageViewStatForGuideDialog, LicensePageViewStatForGuideDialogProps } from '../guides/LicensePageViewStatDialog';
import { GuideReferencedByDialog, GuideReferencedByDialogProps } from '../guides/GuideReferencedByDialog';
import { GuidesReplacedByDialog, GuidesReplacedByDialogProps } from '../guides/GuidesReplacedByDialog';
import { useConfig } from '../env';

export type Guide = NonNullable<GetGuideQuery['guide']>;

export function Backlog({ guide }: { guide: Guide }) {
  const modalDetails = useModal<RankDetailsDialogProps, unknown>({ data: { guideId: guide.id, docName: guide.docName, docTitle: guide.docTitle } });
  const modalStats = useModal<LicensePageViewStatForGuideDialogProps, undefined>({ data: guide });
  const modalReferencedBy = useModal<GuideReferencedByDialogProps, unknown>({
    data: { guideId: guide.id, docName: guide.docName, docTitle: guide.docTitle },
  });
  const modalReplacedBy = useModal<GuidesReplacedByDialogProps, unknown>({
    data: { guideId: guide.id, docName: guide.docName, docTitle: guide.docTitle },
  });

  const showPageViewsPerLicense = (includeSintef: boolean) => {
    modalStats.open({ id: guide.id, docName: guide.docName, includeSintef });
  };

  const showRankDetails = () => {
    modalDetails.open();
  };

  const showReferencedBy = () => {
    modalReferencedBy.open();
  };

  const showReplacedBy = () => {
    modalReplacedBy.open();
  };

  return (
    <StyledPaper sx={{ minHeight: 260 }}>
      <Typography variant="h5" component="h3">
        Planleggingsdetaljer
      </Typography>
      <Details
        guide={guide}
        showPageViewsPerLicense={showPageViewsPerLicense}
        showRankDetails={showRankDetails}
        showReferencedBy={showReferencedBy}
        showReplacedBy={showReplacedBy}
      />
      {modalDetails.isOpen && <RankDetailsDialog modal={modalDetails} />}
      {modalStats.isOpen && <LicensePageViewStatForGuideDialog modal={modalStats} />}
      {modalReferencedBy.isOpen && <GuideReferencedByDialog modal={modalReferencedBy} />}
      {modalReplacedBy.isOpen && <GuidesReplacedByDialog modal={modalReplacedBy} />}
    </StyledPaper>
  );
}

function Details({
  guide,
  showPageViewsPerLicense,
  showRankDetails,
  showReferencedBy,
  showReplacedBy,
}: {
  guide: NonNullable<Guide>;
  showPageViewsPerLicense(includeSintef: boolean): void;
  showRankDetails(): void;
  showReferencedBy(): void;
  showReplacedBy(): void;
}) {
  const config = useConfig();

  const [includeSintefOnPageViews, setIncludeSintefOnPageViews] = useState(true);

  const statisticsApiUrl = config?.statisticsApiUrl ? config.statisticsApiUrl + `/industry/${guide.docId}` : '';

  const score = Object.keys(guide.planningScores!)
    .filter((x) => x !== '__typename')
    .map((x) => x as keyof typeof guide.planningScores)
    .map((x) => guide.planningScores![x] as { score: number })
    .reduce((acc, elem) => acc + elem.score, 0);

  const pageViews = guide.planningScores?.pageViews;
  return (
    <>
      <List>
        <ListItem sx={{ paddingTop: '2px', paddingBottom: '2px', alignItems: 'flex-start' }}>
          <ListLabel>
            Score:
            <IconButton
              sx={{ padding: '2px' }}
              href="https://byggforsk.atlassian.net/wiki/spaces/FOU/pages/263028741"
              target="_blank"
              rel="noopener noreferrer"
              size="large">
              <Info fontSize="small" />
            </IconButton>
          </ListLabel>
          <ListItemText>
            <Button onClick={showRankDetails} sx={{ pl: 0, minWidth: 0 }}>
              {score}
            </Button>
          </ListItemText>
        </ListItem>
        <ListItem sx={{ paddingTop: '2px', paddingBottom: '2px', alignItems: 'flex-start' }}>
          <ListLabel>Visninger:</ListLabel>
          <ListItemText>
            <Stack>
              <span
                title={`${guide.docName?.startsWith('BVN') ? 'BVN' : 'BFS'}: nr ${
                  (pageViews?.placement?.index ?? 0) + 1
                } av ${pageViews?.placement?.total}`}>
                {Number(includeSintefOnPageViews ? pageViews!.value : pageViews!.pageViewsExternal).toLocaleString('no-NO')} visninger de siste 365
                dagene.
              </span>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeSintefOnPageViews}
                      onChange={() => setIncludeSintefOnPageViews(!includeSintefOnPageViews)}
                    />
                  }
                  label="Inkludert oppslag fra SINTEF Community"
                />
              </FormGroup>
            </Stack>
          </ListItemText>
        </ListItem>

        <ListItem sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
          <ListLabel></ListLabel>
          <ListItemText>
            <Button href={statisticsApiUrl} target="_blank" endIcon={<OpenInNew />} sx={{ pl: 0, justifyContent: 'flex-start' }}>
              Statistikk
            </Button>
          </ListItemText>
        </ListItem>

        <ListItem sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
          <ListLabel>Henvisninger:</ListLabel>
          <ListItemText disableTypography>
            <Stack direction="row" flexWrap="wrap" columnGap={1}>
              <Button onClick={showReferencedBy} sx={{ pl: 0, justifyContent: 'flex-start' }}>
                Anvisninger som henviser til denne
              </Button>
              <Button onClick={showReplacedBy} sx={{ pl: 0, justifyContent: 'flex-start' }}>
                Anvisninger som erstattes av denne
              </Button>
            </Stack>
          </ListItemText>
        </ListItem>
      </List>
    </>
  );
}
